import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { listArticles, deleteArticle } from "../../../api/articles";
import type { Article } from "../../../types/article";
import { CATEGORY_LABELS } from "../../../types/article";
import { SearchBar } from "../../../components/articles/SearchBar";
import { ArticleCard } from "../../../components/articles/ArticleCard";
import { Modal, Button, Toast, useToast } from "@hiya/ui";
import iconPlus from "../../../assets/icon-plus.svg";

export function ExploreArticlesPage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Article | null>(null);
  const { toast, showToast } = useToast();

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listArticles();
      setArticles(data);
    } catch {
      showToast("Failed to load articles");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void loadArticles();
  }, [loadArticles]);

  const filtered = useMemo(() => {
    if (!search.trim()) return articles;
    const q = search.toLowerCase();
    return articles.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.category.toLowerCase().includes(q) ||
        (CATEGORY_LABELS[a.category] ?? "").toLowerCase().includes(q),
    );
  }, [articles, search]);

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteArticle(deleteTarget.id);
      setArticles((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      showToast("Article deleted");
    } catch {
      showToast("Failed to delete article");
    } finally {
      setDeleteTarget(null);
    }
  }

  return (
    <div>
      <Toast message={toast.message} visible={toast.visible} />

      <div className="flex gap-4 mb-6">
        <SearchBar value={search} onChange={setSearch} />
        <button
          type="button"
          onClick={() => navigate("/dashboard/content/articles/create")}
          className="h-[51px] px-6 rounded-2xl bg-btn-action text-text-btn text-base font-semibold flex items-center gap-2 whitespace-nowrap"
        >
          <img src={iconPlus} alt="" width={20} height={20} className="invert" />
          Create Article
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-5">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[200px] rounded-xl bg-bg-secondary animate-pulse"
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-text-secondary text-base mb-4">
            {search ? "No articles match your search" : "No articles yet"}
          </p>
          {!search && (
            <button
              type="button"
              onClick={() => navigate("/dashboard/content/articles/create")}
              className="h-[51px] px-6 rounded-2xl bg-btn-action text-text-btn text-base font-semibold"
            >
              Create Article
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-5">
          {filtered.map((article) => (
            <ArticleCard
              key={article.id}
              article={article}
              onEdit={() =>
                navigate(`/dashboard/content/articles/${article.id}/edit`)
              }
              onDelete={() => setDeleteTarget(article)}
            />
          ))}
        </div>
      )}

      <Modal
        open={deleteTarget !== null}
        title="Delete article"
        onClose={() => setDeleteTarget(null)}
      >
        <p className="text-sm text-text-primary leading-[1.5]">
          Are you sure you want to delete{" "}
          <span className="text-brand-brown font-semibold">
            {deleteTarget?.title}
          </span>{" "}
          article? It will be deleted from the app
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete article
          </Button>
        </div>
      </Modal>
    </div>
  );
}
